import { adaptSchemaV2 } from '../../scripts/mapSchemaV2.mjs'
import type { MapSchemaV2 } from '../schema/v2/types'
import { FLAG_BASE_ONLY, MmtgElementType } from '../geometry/mmtg/format'
import { readMmtg, type MmtgFile } from '../geometry/mmtg/reader'
import { geometrySignature, type SignatureTheme } from './geometrySignature'
import { schemaMarkup, type GeometryType, type MapView, type SchemaMarkup } from './schemaMarkup'
import type { VerifyCheck } from './verifyOrder'

const TYPES: ReadonlyArray<readonly [GeometryType, MmtgElementType]> = [
  ['connections', MmtgElementType.Connections],
  ['stations', MmtgElementType.Stations],
  ['additionals', MmtgElementType.Additionals],
  ['transitions', MmtgElementType.Transitions],
]

const DAY_VIEWS: readonly MapView[] = ['light', 'dark']

/** The late-night frame is written on the dark palette (`metro-server` `artifact.nightView`). */
const SIGNATURE_THEME: Readonly<Record<MapView, SignatureTheme>> = {
  light: 'light',
  dark: 'dark',
  night: 'dark',
}

/** The late-night frame's files (TASK-1136); a bundle carries all of them or none. */
export const NIGHT_FRAME_FILES: readonly string[] = TYPES.map(([type]) => `${type}-night.mmtg`)

/** A bundle's unpacked files by name, already checked against their sha256. */
export type BundleBytes = ReadonlyMap<string, ArrayBuffer>

/**
 * Checks an unpacked map bundle with the code that will run it (spec §3.5,
 * steps 2–5; the hash steps are the downloader's): the manifest and schema
 * `format` are supported, the schema decodes through `adaptSchemaV2` (what
 * `fetchSchema` runs), every MMTG file the renderer reads opens with
 * `readMmtg` and carries the signature of the schema markup, and the files
 * reference each other completely (every stop, connection and drawn transfer
 * has geometry, nothing else does; the transfer order is a list of ids).
 * A bundle with a late-night frame (TASK-1136) is checked the same way on its
 * `*-night.mmtg` set against `paths.night` and `transitions-order-night.json`;
 * a bundle without one — London, or a pre-night server — is valid as is, one
 * with part of the set is not. Returns the first failing check in
 * `VERIFY_ORDER`, `''` when all pass.
 */
export async function verifyBundle(
  manifestFormat: number,
  files: BundleBytes,
  supportedFormat: number,
): Promise<VerifyCheck | ''> {
  if (manifestFormat > supportedFormat) return 'format'
  const decoder = new TextDecoder()
  const schemaBytes = files.get('schema.json')
  if (schemaBytes === undefined) return 'schema'
  let raw: unknown
  try {
    raw = JSON.parse(decoder.decode(schemaBytes))
  } catch {
    return 'schema'
  }
  const format = (raw as { format?: unknown } | null)?.format
  if (typeof format !== 'number' || format > supportedFormat) return 'format'
  let markup: SchemaMarkup
  try {
    adaptSchemaV2(raw as MapSchemaV2)
    markup = schemaMarkup(raw)
  } catch {
    return 'schema'
  }

  const nightFiles = NIGHT_FRAME_FILES.filter((name) => files.has(name)).length
  if (nightFiles !== 0 && nightFiles !== NIGHT_FRAME_FILES.length) return 'signature'
  const views = nightFiles === 0 ? DAY_VIEWS : [...DAY_VIEWS, 'night' as const]

  const opened = new Map<string, MmtgFile>()
  for (const view of views) {
    const viewMarkup = markup.markup(view)
    const interchanges = markup.interchanges(view)
    for (const [type, elementType] of TYPES) {
      const name = `${type}-${view}.mmtg`
      const bytes = files.get(name)
      if (bytes === undefined) return 'signature'
      let file: MmtgFile
      try {
        file = readMmtg(bytes, elementType)
      } catch {
        return 'signature'
      }
      const pairs = []
      for (const entry of file.entries) {
        const path =
          (entry.flags & FLAG_BASE_ONLY) !== 0
            ? interchanges.get(entry.id)
            : viewMarkup[type].get(entry.id)
        if (path === undefined) return 'signature'
        pairs.push({ id: entry.id, path })
      }
      if ((await geometrySignature(pairs, SIGNATURE_THEME[view], file.version)) !== file.signature)
        return 'signature'
      opened.set(name, file)
    }
  }

  for (const view of views) {
    const expected = markup.ids(view)
    const ids = (type: GeometryType): Set<string> =>
      new Set(opened.get(`${type}-${view}.mmtg`)?.entries.map((e) => e.id) ?? [])
    if (!sameSet(ids('stations'), expected.stopIds)) return 'references'
    if (!sameSet(ids('connections'), expected.connectionIds)) return 'references'
    if (!sameSet(ids('transitions'), expected.drawnTransferIds)) return 'references'
  }
  for (const name of ['transitions-order.json', 'transitions-order-night.json']) {
    const order = files.get(name)
    if (order === undefined) continue
    try {
      const ids: unknown = JSON.parse(decoder.decode(order))
      if (!Array.isArray(ids) || !ids.every((id) => typeof id === 'string')) return 'references'
    } catch {
      return 'references'
    }
  }
  return ''
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const v of a) if (!b.has(v)) return false
  return true
}
